import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveErrorMessageKey } from '@constants/error-codes/error-code-map';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { QueryFunction, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { computeQueryEnabled } from '@utils/auth/compute-query-enabled';
import { RequestError } from '@utils/request/request.type';
import { showToastErrorRequest } from '@utils/show-toast-error-request/show-toast-error-request';

import { useRefreshTokenHandler } from './auth/use-refresh-token-handler';
import { useTokenManagement } from './auth/use-token-management';
import { useToast } from './use-toast/use-toast';

export type GenericQueryOptions<
  TQueryFnData = unknown,
  TError = RequestError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn' | 'queryKey'> & {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  hideError?: boolean;
};

export const useGenericQuery = <
  TQueryFnData = unknown,
  TError = RequestError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  key: TQueryKey,
  fn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: GenericQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  hideError?: boolean
): UseQueryResult<TData, TError> => {
  const { isSelectVenueActive, isPublicEventReportPage } = useContext(FeatureFlagContext);
  const toast = useToast();
  const { t } = useTranslation();

  const { getTokens } = useTokenManagement();
  const { accessToken, refreshToken } = getTokens();

  const { createRefreshAndRetryHandler } = useRefreshTokenHandler();
  const handleRefreshAndRetry = createRefreshAndRetryHandler<
    Parameters<QueryFunction<TQueryFnData, TQueryKey>>[0],
    TQueryFnData | Promise<TQueryFnData>
  >((context) => fn(context));

  // QUERY FUNCTION WITH IMMEDIATE RETRY ON 401
  const queryFnWithRetry: QueryFunction<TQueryFnData, TQueryKey> = async (context) => {
    try {
      return await fn(context);
    } catch (error: unknown) {
      if (error instanceof RequestError && error.status === 401) {
        return await handleRefreshAndRetry(context);
      }
      // If not 401, propagate the error
      throw error;
    }
  };

  // Destructure custom options out to avoid passing them to useQuery
  const { onSuccess, onError, hideError: optionsHideError, enabled: optionsEnabled, ...queryOptions } = options ?? {};

  // Determine if query should be enabled
  const finalEnabled = computeQueryEnabled({
    isPublicEventReportPage,
    accessToken,
    refreshToken,
    optionsEnabled: optionsEnabled as boolean | undefined,
  });

  const query = useQuery<TQueryFnData, TError, TData, TQueryKey>({
    queryKey: key,
    queryFn: queryFnWithRetry,
    ...(queryOptions as Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>),
    enabled: finalEnabled,
    retry: (failureCount, error) => {
      // Don't retry on 401 - already handled by queryFnWithRetry
      if (error instanceof RequestError && error.status === 401) {
        return false;
      }
      if (isSelectVenueActive && error instanceof RequestError) {
        return false;
      }
      if (queryOptions?.retry !== false) {
        const numberOfRetry = queryOptions?.retry && typeof queryOptions?.retry === 'number' ? queryOptions?.retry : 4;
        return failureCount < numberOfRetry;
      }
      return false;
    },
  });

  // Handle error state - show toast and call onError callback
  useEffect(() => {
    if (query.isError) {
      const error = query.error;
      if (onError) onError(error);
      if (error instanceof RequestError) {
        const hError = optionsHideError ?? hideError;
        if (!hError && showToastErrorRequest(error)) {
          const messageKey = resolveErrorMessageKey(error.code);
          toast.push({
            title: messageKey ? t(messageKey) : error.message,
            text: '',
            type: 'error',
            disableTimeOut: true,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  // Handle success state - call onSuccess callback
  useEffect(() => {
    if (query.isSuccess) {
      if (onSuccess) onSuccess(query.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.data]);

  return query;
};
